import axios, { AxiosResponse } from 'axios';
import { CLOUD_FUNCTION_URL } from '../../env';
import { generateUuidV4 } from '../../util/uuid';
import { log } from '../../util/log';
import { MetadataError } from '@ep/error-handling';
import { FUNCTION_UNKNOWN_FAILURE } from '../../util/global_variables';
import { type AuthHeader, getAuthHeaderForUser, handleAuthError } from '../auth/auth.util';

const SOURCE = 'file.ts';

export type SignedUrlItem = {
  file: string;
  url: string;
};

/**
 * Download signed files directly from cloud storage.
 * @param {string} bucket Name of GCS bucket to download file from.
 * @param {string} path GCS path of the file to download.
 * @returns {Promise<void>}
 */
export const downloadFileFromSignedUrl = async (bucket: string, path: string): Promise<void> => {
  try {
    const auth_header: AuthHeader = await getAuthHeaderForUser();

    const response: AxiosResponse = await axios({
      method: 'post',
      url: `${CLOUD_FUNCTION_URL}/getSignedFileUrls`,
      data: {
        bucket,
        path
      },
      headers: {
        ...auth_header
      }
    });

    const urls: Array<SignedUrlItem> | undefined = response?.data?.data?.urls;

    if (!urls || !urls.length) {
      throw new Error('No signed URLs returned.');
    }

    // Single file - direct download, browser stays on page when response is attachment
    if (urls.length === 1) {
      window.location.href = urls[0].url;
      return;
    }

    // Multiple files - open each in a new tab to trigger download
    urls.forEach(({ url }: { url: string }) => {
      window.open(url, '_blank');
    });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    log(
      'error',
      new MetadataError(
        err instanceof Error
          ? err.message
          : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'downloadFileFromSignedUrl'),
        {
          bucket: bucket,
          path: path
        },
        tracking_id
      )
    );
    handleAuthError(err);
    throw new Error(`Error: downloadFileFromSignedUrl: ${JSON.stringify(err)}.`);
  }
};
